import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {GroupHeader} from '@/components/help-icon'
import {NivelApilado, NivelDonut} from '@/components/bateria/nivel-charts'
import {NivelTag} from '@/components/bateria/nivel-tag'
import {TaxonomiaBloque} from '@/components/bateria/taxonomia-bloque'
import {type AgregadoCuestionario, type NivelDistribucion} from '@/hooks/bateria/use-bateria'
import {totalPersonas} from '@/lib/bateria'
import type {EntradaNivel} from '@/lib/bateria-charts'

const CUESTIONARIOS = ['intralaboral', 'extralaboral', 'estres'] as const

/** Sin ayuda propia: qué es cada cuestionario se dice una vez, en el encabezado de la sección (U4). */
function LecturaCuestionario({label, dist}: {label: string; dist: NivelDistribucion}) {
  const {t} = useTranslation()
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="text-sm font-medium text-foreground">{label}</span>
      </span>
      <span className="flex items-center gap-2">
        <NivelTag dist={dist} />
        {dist.promedio_transformado !== null && (
          <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
            {t('admin.bateria.resultados.promedio', {value: dist.promedio_transformado.toFixed(1)})}
          </span>
        )}
        <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
          {t('admin.bateria.resultados.nPersonas', {count: totalPersonas(dist)})}
        </span>
      </span>
    </div>
  )
}

/**
 * Las gráficas no se re-montan al cambiar de alcance: ApexCharts anima en vez de parpadear.
 * `segmentTotalN` es la población del segmento: si el n calificado es menor, se dice.
 */
export function AgregadoBloque({agregado, segmentTotalN}: {agregado: AgregadoCuestionario; segmentTotalN?: number}) {
  const {t} = useTranslation()
  const [openDimensiones, setOpenDimensiones] = useState(false)

  const cuestionarios = CUESTIONARIOS.filter((c) => agregado.cuestionarios[c])
  const hayDominios = Object.keys(agregado.dominios).length > 0
  const dimensiones = Object.keys(agregado.dimensiones).length

  // El filtro se rehace dentro del memo: una identidad nueva por render reaplica las opciones de
  // ApexCharts y mata la animación de cambio de alcance.
  const entradas = useMemo<EntradaNivel[]>(
    () =>
      [
        ...CUESTIONARIOS.filter((c) => agregado.cuestionarios[c]).map((c) => ({
          clave: c,
          label: t(`admin.bateria.resultados.cuestionario.${c}`),
          dist: agregado.cuestionarios[c],
        })),
        {clave: 'total_general', label: t('admin.bateria.resultados.totalGeneral'), dist: agregado.total_general},
      ].filter((e) => e.dist.n_validos > 0),
    [agregado, t]
  )

  const scoredN = agregado.total_general.n_validos
  const nonCalificables = segmentTotalN !== undefined ? segmentTotalN - scoredN : 0

  return (
    <div className="space-y-4" data-testid="agregado-view">
      <section className="surface p-4 sm:p-5">
        <div className="mb-3">
          <GroupHeader
            label={t('admin.bateria.resultados.cuestionariosTitle')}
            help={t('admin.bateria.resultados.cuestionariosHelp')}
            testId="cuestionarios-help"
          />
        </div>
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center">
          <div
            className="w-full shrink-0 self-center border-border sm:max-w-[220px] lg:self-center lg:border-r lg:pr-6"
            data-testid="total-general-donut">
            <NivelDonut
              distribucion={agregado.total_general.distribucion}
              label={t('admin.bateria.resultados.totalGeneral')}
            />
          </div>
          <div className="min-w-0 flex-1">
            <NivelApilado
              entradas={entradas}
              ariaLabel={t('admin.bateria.resultados.distribucionLabel')}
              testId="cuestionarios-apilado-chart"
            />
          </div>
        </div>
        <div className="mt-2 divide-y divide-border border-t border-border pt-1">
          {cuestionarios.map((c) => (
            <LecturaCuestionario
              key={c}
              label={t(`admin.bateria.resultados.cuestionario.${c}`)}
              dist={agregado.cuestionarios[c]}
            />
          ))}
          <LecturaCuestionario label={t('admin.bateria.resultados.totalGeneral')} dist={agregado.total_general} />
        </div>
        <p
          className="mt-3 border-t border-border pt-3 font-mono text-[11px] text-muted-foreground"
          data-testid="agregado-base-n">
          {t('admin.bateria.resultados.baseLectura', {count: scoredN})}
          {nonCalificables > 0 && (
            <span className="text-tone-amber-fg" data-testid="agregado-no-calificables">
              {' · '}
              {t('admin.bateria.resultados.noCalificables', {count: nonCalificables})}
            </span>
          )}
        </p>
      </section>

      {hayDominios && (
        <section className="surface p-4 sm:p-5">
          <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('admin.bateria.resultados.dominiosTitle')}
          </p>
          <div className="mt-3">
            <TaxonomiaBloque
              data={agregado.dominios}
              ariaLabel={t('admin.bateria.resultados.comparacionDominiosLabel')}
              idPrefix="dominio"
              testId="dominios-comparacion"
            />
          </div>
        </section>
      )}

      {dimensiones > 0 && (
        <details
          className="surface p-4 sm:p-5"
          data-testid="dimensiones-details"
          open={openDimensiones}
          onToggle={(e) => setOpenDimensiones((e.target as HTMLDetailsElement).open)}>
          <summary className="flex cursor-pointer items-center gap-1.5 font-mono text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('admin.bateria.resultados.dimensionesTitle')} · {dimensiones}
          </summary>
          <div className="mt-3">
            <TaxonomiaBloque
              data={agregado.dimensiones}
              ariaLabel={t('admin.bateria.resultados.comparacionDimensionesLabel')}
              idPrefix="dimension"
              testId="dimensiones-comparacion"
            />
          </div>
        </details>
      )}
    </div>
  )
}
